import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ReactGA from "react-ga4";
import {
  signInWithCustomToken,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  authenticateEmailAPICall,
  setLinkedEmailPasswordAPICall,
  canLinkAPICall,
  getCurrentUserApi,
  getCurrentUserAPINoRedirectCall,
  getCustomTokenAPICall,
  getPlatformSubscriptionAPICall,
  getProductQuotaAPICall,
  linkEmailAPICall,
  linkPhoneNumberAPICall,
  loginWithGoogleAPICall,
  loginWithSSOAPICall,
} from "../API/users/getCurrentUser";
import { updateUserApi } from "../API/users/updateUser";
import { refreshTokenApi } from "../API/users/refreshToken";
import { deleteUserApi } from "../API/users/deleteUser";
import {
  registerUserApi,
  registerUserWiwthSSOAPICALL,
} from "../API/users/registerUser";
import LocalStorage from "../Helpers/LocalStorage";
import { updateUserProfilePicturesApi } from "../API/users/setUserProfilePicture";
import {
  getCallLanguagesAPI,
  getTranslationsLanguagesAPI,
} from "../API/users/getTranslationsLanguages";
import { logoutUserAPICAll } from "../API/users/logoutUser";
import { useCookies } from "react-cookie";
import { Purchases } from "@revenuecat/purchases-js";
import {
  cancelStripeSubscriptionAPICall,
  getStripePaymentLinkAPICall,
  hadFreeTrialAPICall,
} from "../API/payments/paymentsService";
import { unregister } from "../serviceWorker";
import { getNumberOfReferralsAPICall } from "../API/users/numberOfReferrals";
import { useLinksquaredService } from "../Context/LinksquaredContext";
import {
  SUBSCRIPTION_LEVEL_PRO,
  SUBSCRIPTION_LEVEL_ULTRA,
  SUBSCRIPTION_LEVEL_PLATFORM,
  SUBSCRIPTION_LEVEL_UNSUBSCRIBED,
  SUBSCRIPTION_LEVEL_REFERRALS,
  SUBSCRIPTION_LEVEL_PREMIUM,
} from "../Constants/constantValues";
import {
  isSubscriptionLevelEnoughForAccessLevel,
} from "../Helpers/subscriptions/SubscriptionsHelper";
import { closeAllSecondaryWindows } from "../Helpers/navigationHelper";
import languageSyncManager from "../Helpers/callHelper/LanguageSyncHelper";

export const AuthContext = createContext({
  user: [],
  userCustomerInfo: [],
  isAuthenticated: [],
  translationsLanguages: [],
  callTranslationsLanguages: [],
  referrals: [],
  callTranslationsLanguagesRef: [],
  subscriptionLevel: [],
  subscription: [],
  offering: [],
  platformPresentations: null,
  platformVideoTool: null,
  platformMeetings: null,
  platformConferences: null,
  toolProjectIds: {},
  getProductQuota: () => {},
  userRef: [],
  anonUser: [],
  authHasCompleted: [],
  disableAuthCallback: [],
  disableAuthObserver: [],
  signInWithPhone: () => { },
  verifyOtpCodeAndSignIn: () => { },
  createUser: () => { },
  getCurrentUser: () => { },
  signOutFirebase: () => { },
  updateUser: () => { },
  refreshToken: () => { },
  deleteUser: () => { },
  updateUserProfilePictures: () => { },
  getTranslationsLanguages: () => { },
  handleLoginUser: () => { },
  updateUserWithNewPushToken: () => { },
  setPushToken: () => { },
  pushToken: [],
  hadFreeTrial: () => { },
  getStripePaymentLink: () => { },
  cancelStripeSubscription: () => { },
  checkSubscriptions: () => { },
  getCustomToken: () => { },
  signInWithCustomAcessToken: () => { },
  getMicrosoftSSOLoginLink: () => { },
  handleSignOutUser: () => { },
  getCallTranslationsLanguages: () => { },
  getNumberOfReferrals: () => { },
  getGoogleSSOLoginLink: () => { },
  registerUserWiwthSSO: () => { },
  getPlatformSubscription: () => { },
  handleLogoutUser: () => { },
  getCurrentUserNoRedirect: () => { },
  checkIfUserHasPermisionFor: () => { },
  handleLoginAnonUser: () => { },
  canLink: () => { },
  linkPhoneNumber: () => { },
  linkEmail: () => { },
  authenticateEmail: () => { },
  setLinkedEmailPassword: () => { },
});

const AuthContextProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies(["notificationsModal"]);
  const [pushToken, setPushToken] = useState(null);
  const [user, setUser] = useState(undefined);
  const [translationsLanguages, setTranslationsLanguages] = useState([]);
  const [callTranslationsLanguages, setCallTranslationsLanguages] = useState(
    []
  );
  const callTranslationsLanguagesRef = useRef([]);
  const [userCustomerInfo, setUserCustomerInfo] = useState(null);
  const [referrals, setReferrals] = useState(0);

  const [subscriptionLevel, setSubscriptionLevel] = useState("not_subscribed");
  const [subscription, setSubscription] = useState(null);
  const [offering, setOffering] = useState(null);
  const [platformPresentations, setPlatformPresentations] = useState(null);
  const [platformVideoTool, setPlatformVideoTool] = useState(null);
  const [platformMeetings, setPlatformMeetings] = useState(null);
  const [platformConferences, setPlatformConferences] = useState(null);
  const [toolProjectIds, setToolProjectIds] = useState({});
  const [authHasCompleted, setAuthHasCompleted] = useState(null);
  const userRef = useRef();
  const anonUser = useRef(false);
  const { setUserDetails } = useLinksquaredService();
  const disableAuthCallback = useRef(false);
  const disableAuthObserver = useRef(false);
  const getCallTranslationsLanguagesFnRef = useRef(() => {});
  const checkSubscriptionsFnRef = useRef(() => {});
  const getPlatformSubscriptionFnRef = useRef(() => {});
  const handleSignOutUserFnRef = useRef(() => {});

  const REVENUE_CAT_KEY = process.env.REACT_APP_REVENUE_CAT_KEY;

  const handleLoginUser = useCallback((user) => {
    // TODO: Remove - testing onboarding
    // delete user.name;
    // delete user.language;
    // delete user.call_language;

    userRef.current = user;
    setUser(user);
    setUserDetails(user);
    setAuthHasCompleted(true);

    // Get current languages
    getCallTranslationsLanguagesFnRef.current(
      function () { },
      function () { }
    );
  }, [setUserDetails]);

  const handleLoginAnonUser = useCallback((user) => {
    // TODO: Remove - testing onboarding
    // delete user.name;
    // delete user.language;
    // delete user.call_language;

    userRef.current = user;
    anonUser.current = true;
    setUser(user);
    setUserDetails(user);
  }, [setUserDetails]);

  const handleLogoutUser = useCallback(async () => {
    // SET GA ANALYTICS userID null
    ReactGA.set({ user_id: null });
    anonUser.current = false;
    userRef.current = null;
    LocalStorage.removeAuthenticationToken();
    LocalStorage.removeRefreshToken();
    LocalStorage.removeSSOFlag();
    LocalStorage.setBridgeOptedOut(true);
    setUser(null);
    setAuthHasCompleted(true);

    await closeAllSecondaryWindows();
  }, []);

  const checkIfUserHasPermisionFor = useCallback((
    accessLevel,
    allowedCompletion,
    unallowedCompletion,
    unsubscribedCompletion
  ) => {
    checkSubscriptionsFnRef.current((subscriptionLevel) => {
      if (subscriptionLevel === SUBSCRIPTION_LEVEL_UNSUBSCRIBED) {
        unsubscribedCompletion();
        return;
      }

      const valid = isSubscriptionLevelEnoughForAccessLevel(
        subscriptionLevel,
        accessLevel
      );
      if (valid) {
        allowedCompletion();
      } else {
        unallowedCompletion();
      }
    });
  }, []);

  const checkSubscriptions = useCallback(async (completion) => {
    try {
      const customerInfo =
        await Purchases.getSharedInstance().getCustomerInfo();
      
      if (
        customerInfo.entitlements.active.Pro &&
        customerInfo.entitlements.active.Pro.isActive
      ) {
        setSubscription(customerInfo.entitlements.active.Pro);
        setSubscriptionLevel(SUBSCRIPTION_LEVEL_PRO);
        completion(SUBSCRIPTION_LEVEL_PRO);
        return;
      }

      if (
        customerInfo.entitlements.active.Premium &&
        customerInfo.entitlements.active.Premium.isActive
      ) {
        setSubscription(customerInfo.entitlements.active.Premium);
        setSubscriptionLevel(SUBSCRIPTION_LEVEL_PREMIUM);
        completion(SUBSCRIPTION_LEVEL_PREMIUM);
        return;
      }

      if (
        customerInfo.entitlements.active.Ultra &&
        customerInfo.entitlements.active.Ultra.isActive
      ) {
        setSubscription(customerInfo.entitlements.active.Ultra);
        setSubscriptionLevel(SUBSCRIPTION_LEVEL_ULTRA);
        completion(SUBSCRIPTION_LEVEL_ULTRA);

        return;
      }

      getPlatformSubscriptionFnRef.current(
        (response) => {

          const platformSubscription = response.data.platform_subscription;
          const offering = response.data.offering;
          const presentations = response.data.tool_data?.presentations;
          const videoTool = response.data.tool_data?.video_tool;
          const meetings = response.data.tool_data?.meetings;
          const conferences = response.data.tool_data?.conferences;

          setOffering(offering);
          setPlatformPresentations(presentations);
          setPlatformVideoTool(videoTool);
          setPlatformMeetings(meetings);
          setPlatformConferences(conferences);
          setToolProjectIds(response.data.tool_project_ids || {});

          if (
            platformSubscription &&
            platformSubscription.active &&
            platformSubscription.status === "active"
          ) {
            setSubscription(platformSubscription);
            setSubscriptionLevel(SUBSCRIPTION_LEVEL_PLATFORM);
            completion(SUBSCRIPTION_LEVEL_PLATFORM);
            return;
          } else if (
            customerInfo.entitlements.active.Referrals &&
            customerInfo.entitlements.active.Referrals.isActive
          ) {
            setSubscription(customerInfo.entitlements.active.Referrals);
            setSubscriptionLevel(SUBSCRIPTION_LEVEL_REFERRALS);
            completion(SUBSCRIPTION_LEVEL_REFERRALS);
            return;
          } else {
            setSubscription(null);
            setSubscriptionLevel(SUBSCRIPTION_LEVEL_UNSUBSCRIBED);
            completion(SUBSCRIPTION_LEVEL_UNSUBSCRIBED);
            return;
          }
        },
        () => { }
      );
      // access latest customerInfo
    } catch (e) {
      // Handle errors fetching customer info
    }
  }, []);
  checkSubscriptionsFnRef.current = checkSubscriptions;

  const signInWithPhone = useCallback((phoneNumber, appVerifier, onSucces, onError) => {
    signInWithPhoneNumber(auth, phoneNumber, appVerifier)
      .then((confirmationResult) => {
        // SMS sent. Prompt user to type the code from the message, then sign the
        // user in with confirmationResult.confirm(code).
        onSucces(confirmationResult);
        //   window.confirmationResult = confirmationResult;
        // ...
      })
      .catch((error) => {
        onError(error);
      });
  }, []);

  const signInWithCustomAcessToken = useCallback((customAccessToken, onSucces, onError) => {
    signInWithCustomToken(auth, customAccessToken)
      .then((userCredential) => {
        // Signed in
        const userResponse = userCredential.user;
        LocalStorage.setAuthenthicationToken(userResponse.accessToken);
        LocalStorage.setRefreshToken(userResponse.stsTokenManager.refreshToken);
        LocalStorage.removeSSOFlag();
        onSucces(userCredential);
      })
      .catch((error) => {
        onError(error);
      });
  }, []);

  const verifyOtpCodeAndSignIn = useCallback((
    confirmationResult,
    code,
    onSucces,
    onError
  ) => {
    confirmationResult
      .confirm(code)
      .then((result) => {
        onSucces(result);
        // ...
      })
      .catch((error) => {
        onError(error);
        // User couldn't sign in (bad verification code?)
        // ...
      });
  }, []);

  const refreshToken = useCallback(
    async (refreshToken, onSucces, errorCallback) => {
      try {
        await refreshTokenApi(
          refreshToken,
          function (response) {
            onSucces(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback();
      }
    },
    []
  );

  const signOutFirebase = useCallback(() => {
    setCookie("notificationsModal", null);
    const refresh_token = LocalStorage.getRefreshToken();
    signOut(auth)
      .then(() => {
        if (refresh_token && refresh_token != "undefined") {
          handleSignOutUserFnRef.current();
        } else {
          handleLogoutUser();
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [setCookie, handleLogoutUser]);

  const getCurrentUser = useCallback(async (onSucces, errorCallback) => {
    try {
      await getCurrentUserApi(
        function (response) {
          handleLoginUser(response.data.user);
          //SET USER ID FOR GA ANALYTICS
          const user_id = response.data.user.id;
          ReactGA.set({ user_id: user_id });
          const purchases = Purchases.configure(
            REVENUE_CAT_KEY,
            user_id.toString()
          );
          checkSubscriptions();
          onSucces(response);
        },
        function (error) {
          userRef.current = null;
          anonUser.current = false;

          setUser(null);
          setAuthHasCompleted(true);
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback();
    }
  }, [handleLoginUser, checkSubscriptions]);

  const getCurrentUserNoRedirect = useCallback(
    async (onSucces, errorCallback) => {
      try {
        await getCurrentUserAPINoRedirectCall(
          function (response) {
            handleLoginUser(response.data.user);
            //SET USER ID FOR GA ANALYTICS
            const user_id = response.data.user.id;
            ReactGA.set({ user_id: user_id });
            const purchases = Purchases.configure(
              REVENUE_CAT_KEY,
              user_id.toString()
            );
            checkSubscriptions();
            onSucces(response);
          },
          function (error) {
            console.log("error", error);
            userRef.current = null;
            anonUser.current = false;
            setUser(null);
            setAuthHasCompleted(true);
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback();
      }
    },
    [handleLoginUser, checkSubscriptions]
  );

  const getTranslationsLanguages = useCallback(
    async (onSucces, errorCallback) => {
      try {
        await getTranslationsLanguagesAPI(
          function (response) {
            onSucces(response);
            setTranslationsLanguages(response.data.languages);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback();
      }
    },
    []
  );

  const getCallTranslationsLanguages = useCallback(
    async (onSucces, errorCallback) => {
      try {
        await getCallLanguagesAPI(
          function (response) {
            onSucces(response);
            callTranslationsLanguagesRef.current = response.data.languages;
            setCallTranslationsLanguages(response.data.languages);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback();
      }
    },
    []
  );
  getCallTranslationsLanguagesFnRef.current = getCallTranslationsLanguages;

  const getNumberOfReferrals = useCallback(async (onSucces, errorCallback) => {
    try {
      await getNumberOfReferralsAPICall(
        function (response) {
          onSucces(response);
          setReferrals(response.data.referrals);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback();
    }
  }, []);

  const createUser = useCallback(async (data, onSuccess, errorCallback) => {
    try {
      await registerUserApi(
        data,
        function (response) {
          handleLoginUser(response.data.user);
          const user_id = response.data.user.id;
          const purchases = Purchases.configure(
            REVENUE_CAT_KEY,
            user_id.toString()
          );
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, [handleLoginUser]);

  const getCustomToken = useCallback(async (onSuccess, errorCallback) => {
    try {
      await getCustomTokenAPICall(
        function (response) {
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const getPlatformSubscription = useCallback(
    async (onSuccess, errorCallback) => {
      try {
        await getPlatformSubscriptionAPICall(
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );
  getPlatformSubscriptionFnRef.current = getPlatformSubscription;

  const getProductQuota = useCallback(
    async (productSlug, projectId, onSuccess, errorCallback) => {
      try {
        await getProductQuotaAPICall(
          productSlug,
          projectId,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updateUser = useCallback(async (data, onSuccess, errorCallback) => {
    try {
      await updateUserApi(
        data,
        function (response) {
          handleLoginUser(response.data.user);
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, [handleLoginUser]);

  const registerUserWiwthSSO = useCallback(
    async (data, onSuccess, errorCallback) => {
      try {
        await registerUserWiwthSSOAPICALL(
          data,
          function (response) {
            handleLoginUser(response.data.user);
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    [handleLoginUser]
  );

  const deleteUser = useCallback(async (reasons, onSucces, errorCallback) => {
    try {
      await deleteUserApi(
        reasons,
        function (response) {
          signOut(auth)
            .then(() => {
              handleLogoutUser();
            })
            .catch((error) => {
              console.log(error);
            });

          onSucces(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, [handleLogoutUser]);

  const updateUserProfilePictures = useCallback(
    async (data, onSuccess, errorCallback) => {
      if(!data || typeof data.get !== 'function' && !data.get('image')){
        return;
      }
      try {
        await updateUserProfilePicturesApi(
          data,
          function (response) {
            handleLoginUser(response.data.user);
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    [handleLoginUser]
  );

  const signOutUser = useCallback(async (onSuccess, errorCallback) => {
    try {
      await logoutUserAPICAll(
        pushToken,
        function (response) {
          setPushToken(null);
          onSuccess(response);
          unregister();
          LocalStorage.removeSSOFlag();
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, [pushToken]);

  const handleSignOutUser = useCallback(() => {
    signOutUser(
      () => {
        handleLogoutUser();
      },
      (error) => {
        console.log(error);
        handleLogoutUser();
      }
    );
  }, [signOutUser, handleLogoutUser]);
  handleSignOutUserFnRef.current = handleSignOutUser;

  const hadFreeTrial = useCallback(async (onSuccess, errorCallback) => {
    try {
      await hadFreeTrialAPICall(
        function (response) {
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);

  const getStripePaymentLink = useCallback(
    async (type, onSuccess, errorCallback) => {
      try {
        await getStripePaymentLinkAPICall(
          type,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const cancelStripeSubscription = useCallback(
    async (onSuccess, errorCallback) => {
      try {
        await cancelStripeSubscriptionAPICall(
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const updateUserWithNewPushToken = useCallback((token) => {
    var objectData = {};
    objectData.push_token = token;
    objectData.push_token_platform = "web";
    updateUser(
      objectData,
      (response) => { },
      (error) => { }
    );
  }, [updateUser]);

  const getMicrosoftSSOLoginLink = useCallback(
    async (tokenOnly = false, onSuccess, errorCallback) => {
      try {
        await loginWithSSOAPICall(
          tokenOnly,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const getGoogleSSOLoginLink = useCallback(
    async (tokenOnly = false, onSuccess, errorCallback) => {
      try {
        await loginWithGoogleAPICall(
          tokenOnly,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const canLink = useCallback(async (dataObject, onSuccess, errorCallback) => {
    try {
      await canLinkAPICall(
        dataObject,
        function (response) {
          onSuccess(response);
        },
        function (error) {
          errorCallback(error);
        }
      );
    } catch (err) {
      errorCallback(err);
    }
  }, []);
  const linkPhoneNumber = useCallback(
    async (token, onSuccess, errorCallback) => {
      try {
        await linkPhoneNumberAPICall(
          token,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const linkEmail = useCallback(
    async (code, email, onSuccess, errorCallback) => {
      try {
        await linkEmailAPICall(
          code,
          email,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const authenticateEmail = useCallback(
    async (email, onSuccess, errorCallback) => {
      try {
        await authenticateEmailAPICall(
          email,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  const setLinkedEmailPassword = useCallback(
    async (password, onSuccess, errorCallback) => {
      try {
        await setLinkedEmailPasswordAPICall(
          password,
          function (response) {
            onSuccess(response);
          },
          function (error) {
            errorCallback(error);
          }
        );
      } catch (err) {
        errorCallback(err);
      }
    },
    []
  );

  useEffect(() => {
    if (window.electron?.onLanguageChanged) {
      const unsubscribe = window.electron.onLanguageChanged((language) => {
        if (userRef.current && userRef.current.call_language !== language) {
          const updatedUser = {
            ...userRef.current,
            call_language: language
          };
          handleLoginUser(updatedUser);
        }
      });
      return typeof unsubscribe === "function" ? unsubscribe : undefined;
    }
  }, []);

  useEffect(() => {
    const cleanup = languageSyncManager.onLanguageChanged((language) => {
      if (userRef.current && userRef.current.call_language !== language) {
        const updatedUser = {
          ...userRef.current,
          call_language: language
        };
        handleLoginUser(updatedUser);
      }
    });

    return cleanup;
  }, []);

  useEffect(() => {
    return () => {
      languageSyncManager.destroy();
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      authHasCompleted,
      user,
      userCustomerInfo,
      signInWithPhone,
      verifyOtpCodeAndSignIn,
      createUser,
      getCurrentUser,
      signOutFirebase,
      updateUser,
      refreshToken,
      deleteUser,
      updateUserProfilePictures,
      getTranslationsLanguages,
      translationsLanguages,
      handleLoginUser,
      updateUserWithNewPushToken,
      setPushToken,
      pushToken,
      hadFreeTrial,
      getStripePaymentLink,
      cancelStripeSubscription,
      checkSubscriptions,
      getCustomToken,
      signInWithCustomAcessToken,
      getMicrosoftSSOLoginLink,
      handleSignOutUser,
      callTranslationsLanguages,
      getCallTranslationsLanguages,
      getNumberOfReferrals,
      referrals,
      getGoogleSSOLoginLink,
      registerUserWiwthSSO,
      callTranslationsLanguagesRef,
      getPlatformSubscription,
      handleLogoutUser,
      getCurrentUserNoRedirect,
      subscription,
      offering,
      platformPresentations,
      platformVideoTool,
      platformMeetings,
      platformConferences,
      toolProjectIds,
      getProductQuota,
      subscriptionLevel,
      checkIfUserHasPermisionFor,
      userRef,
      handleLoginAnonUser,
      anonUser,
      canLink,
      linkPhoneNumber,
      disableAuthCallback,
      disableAuthObserver,
      linkEmail,
      authenticateEmail,
      setLinkedEmailPassword,
    }),
    [
      authHasCompleted,
      user,
      userCustomerInfo,
      signInWithPhone,
      verifyOtpCodeAndSignIn,
      createUser,
      getCurrentUser,
      signOutFirebase,
      updateUser,
      refreshToken,
      deleteUser,
      updateUserProfilePictures,
      getTranslationsLanguages,
      translationsLanguages,
      handleLoginUser,
      updateUserWithNewPushToken,
      pushToken,
      hadFreeTrial,
      getStripePaymentLink,
      cancelStripeSubscription,
      checkSubscriptions,
      getCustomToken,
      signInWithCustomAcessToken,
      getMicrosoftSSOLoginLink,
      handleSignOutUser,
      callTranslationsLanguages,
      getCallTranslationsLanguages,
      getNumberOfReferrals,
      referrals,
      getGoogleSSOLoginLink,
      registerUserWiwthSSO,
      getPlatformSubscription,
      handleLogoutUser,
      getCurrentUserNoRedirect,
      subscription,
      offering,
      platformPresentations,
      platformVideoTool,
      platformMeetings,
      platformConferences,
      toolProjectIds,
      getProductQuota,
      subscriptionLevel,
      checkIfUserHasPermisionFor,
      handleLoginAnonUser,
      canLink,
      linkPhoneNumber,
      linkEmail,
      authenticateEmail,
      setLinkedEmailPassword,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthService = () => useContext(AuthContext);

export default AuthContextProvider;
